/**
 * TestPanel — admin/QA system-test controls for the journey (test environment only).
 *
 * Gated by ?test=1 in the URL so real clients never see it. Renders a small
 * floating button (bottom-right) at the HIGHEST z-index — above every blocking
 * state (e.g. the billing-hold overlay) so we can never lock ourselves out.
 *
 * Toggles let us simulate states without touching real data:
 *   - billingHold — client has an open balance; journey is blurred + blocked.
 *
 * Add a new simulation = add a TOGGLES entry + read tests.<key> where relevant.
 */

import { useState } from 'react'

const TOGGLES = [
  { key: 'billingHold', label: 'Billing hold', hint: 'Client must get current before access' },
]

export default function TestPanel({ tests, onChange }) {
  const [open, setOpen] = useState(false)

  function toggle(key) { onChange({ ...tests, [key]: !tests[key] }) }
  const anyActive = TOGGLES.some(t => tests[t.key])

  return (
    <div style={S.wrap}>
      {open && (
        <div style={S.panel}>
          <div style={S.head}>
            <span style={S.headTitle}>System tests</span>
            <span style={S.headTag}>test env</span>
          </div>
          <div style={S.list}>
            {TOGGLES.map(t => (
              <button key={t.key} onClick={() => toggle(t.key)} style={S.row}>
                <span style={S.rowText}>
                  <span style={S.rowLabel}>{t.label}</span>
                  <span style={S.rowHint}>{t.hint}</span>
                </span>
                <Switch on={!!tests[t.key]} />
              </button>
            ))}
          </div>
        </div>
      )}

      <button onClick={() => setOpen(o => !o)} style={{ ...S.fab, ...(anyActive ? S.fabActive : null) }}>
        <span style={{ fontSize: 14 }}>⚙</span>
        <span>Test</span>
        {anyActive && <span style={S.fabDot} />}
      </button>
    </div>
  )
}

function Switch({ on }) {
  return (
    <span style={{ ...SW.track, background: on ? '#1D9E75' : 'rgba(255,255,255,0.25)' }}>
      <span style={{ ...SW.knob, transform: on ? 'translateX(16px)' : 'translateX(0)' }} />
    </span>
  )
}

const S = {
  wrap: { position: 'fixed', right: 18, bottom: 18, zIndex: 200, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 },
  fab: {
    display: 'inline-flex', alignItems: 'center', gap: 8, position: 'relative',
    fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em',
    color: 'rgba(255,255,255,0.92)', background: 'rgba(6,30,36,0.85)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    border: '0.5px solid rgba(255,255,255,0.18)', borderRadius: 20, padding: '9px 16px',
    cursor: 'pointer', boxShadow: '0 6px 24px rgba(4,21,24,0.4)',
  },
  fabActive: { border: '0.5px solid rgba(29,158,117,0.7)' },
  fabDot: { position: 'absolute', top: -3, right: -3, width: 9, height: 9, borderRadius: '50%', background: '#1D9E75', border: '1.5px solid rgba(6,30,36,0.9)' },
  panel: {
    width: 280, background: 'rgba(6,30,36,0.94)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    border: '0.5px solid rgba(255,255,255,0.16)', borderRadius: 12, boxShadow: '0 16px 48px rgba(4,21,24,0.5)',
    overflow: 'hidden',
  },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '0.5px solid rgba(255,255,255,0.1)' },
  headTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.92)' },
  headTag: { fontFamily: '"DM Mono", monospace', fontSize: 9, color: '#1D9E75', background: 'rgba(29,158,117,0.15)', padding: '3px 8px', borderRadius: 20, letterSpacing: '0.05em' },
  list: { padding: 8 },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', padding: '10px 8px', borderRadius: 8 },
  rowText: { display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 },
  rowLabel: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.92)' },
  rowHint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'rgba(255,255,255,0.45)' },
}

const SW = {
  track: { position: 'relative', display: 'inline-block', width: 34, height: 18, borderRadius: 20, flexShrink: 0, transition: 'background 0.18s' },
  knob: { position: 'absolute', top: 2, left: 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'transform 0.18s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
}
